/**
 * ReferrerNewReferral
 * Form for referrers to submit a new patient referral
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Send, Upload, X, User, Calendar, Mail, Phone,
  FileText, AlertCircle, CheckCircle, Hash, Loader
} from 'lucide-react';
import API_BASE_URL from '../config/api';
import { useReferrerPortalStatus } from '../utils/referrerPortalStatus';
import ReferralsDisabledNotice from '../components/ReferralsDisabledNotice';

const INJURY_SITES = [
  { value: 'KNEE', label: 'Knee', hasSide: true },
  { value: 'HIP', label: 'Hip', hasSide: true },
  { value: 'SHOULDER', label: 'Shoulder', hasSide: true },
  { value: 'ANKLE', label: 'Ankle', hasSide: true },
  { value: 'FOOT', label: 'Foot', hasSide: true },
  { value: 'ELBOW', label: 'Elbow', hasSide: true },
  { value: 'WRIST', label: 'Wrist/Hand', hasSide: true },
  { value: 'LUMBAR_SPINE', label: 'Lumbar Spine', hasSide: false },
  { value: 'CERVICAL_SPINE', label: 'Cervical Spine', hasSide: false },
  { value: 'THORACIC_SPINE', label: 'Thoracic Spine', hasSide: false },
  { value: 'ACHILLES', label: 'Achilles', hasSide: true },
  { value: 'HAMSTRING', label: 'Hamstring', hasSide: true },
  { value: 'CONCUSSION', label: 'Concussion', hasSide: false },
];

function ReferrerNewReferral() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('referrerToken');
  const { loading: statusLoading, submissionsEnabled, notice } = useReferrerPortalStatus();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractionDone, setExtractionDone] = useState(false);
  const extractionAttempted = useRef(false);

  // Form fields
  const [patientName, setPatientName] = useState('');
  const [patientDOB, setPatientDOB] = useState('');
  const [patientEmail, setPatientEmail] = useState('');
  const [patientPhone, setPatientPhone] = useState('');
  const [accNumber, setAccNumber] = useState('');
  const [nhi, setNhi] = useState('');
  const [dateOfInjury, setDateOfInjury] = useState('');
  const [injurySite, setInjurySite] = useState('');
  const [injurySide, setInjurySide] = useState('');
  const [reasonForReferral, setReasonForReferral] = useState('');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [files, setFiles] = useState([]);

  const selectedSiteConfig = INJURY_SITES.find(s => s.value === injurySite);

  // Convert DD/MM/YYYY to YYYY-MM-DD for HTML date inputs
  const convertDateFormat = (dateStr) => {
    if (!dateStr) return '';
    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // DD/MM/YYYY or DD-MM-YYYY
    const match = dateStr.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (match) {
      const [, day, month, year] = match;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return '';
  };

  // Fuzzy match extracted injury site text to our INJURY_SITES enum
  const matchInjurySite = (text) => {
    if (!text) return '';
    const lower = text.toLowerCase().trim();
    // Direct label match
    const direct = INJURY_SITES.find(s => s.label.toLowerCase() === lower || s.value.toLowerCase() === lower);
    if (direct) return direct.value;
    // Partial match
    const partial = INJURY_SITES.find(s =>
      lower.includes(s.label.toLowerCase()) || s.label.toLowerCase().includes(lower)
    );
    if (partial) return partial.value;
    // Keyword match
    const keywords = {
      'KNEE': ['knee'],
      'HIP': ['hip'],
      'SHOULDER': ['shoulder'],
      'ANKLE': ['ankle'],
      'FOOT': ['foot', 'toe', 'metatarsal'],
      'ELBOW': ['elbow'],
      'WRIST': ['wrist', 'hand', 'carpal'],
      'LUMBAR_SPINE': ['lumbar', 'lower back', 'l-spine', 'l spine'],
      'CERVICAL_SPINE': ['cervical', 'neck', 'c-spine', 'c spine'],
      'THORACIC_SPINE': ['thoracic', 'mid back', 't-spine', 't spine'],
      'ACHILLES': ['achilles'],
      'HAMSTRING': ['hamstring'],
      'CONCUSSION': ['concussion', 'head injury', 'tbi'],
    };
    for (const [value, kws] of Object.entries(keywords)) {
      if (kws.some(kw => lower.includes(kw))) return value;
    }
    return '';
  };

  // Run extraction on the first uploaded file
  const runExtraction = async (file) => {
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('document', file);

      const res = await fetch(`${API_BASE_URL}/referrer-referrals/extract`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) return;
      const data = await res.json();
      if (!data.success || !data.extracted) return;

      const ex = data.extracted;

      // Auto-fill only empty fields
      if (ex.name && !patientName) setPatientName(ex.name);
      if (ex.dateOfBirth && !patientDOB) setPatientDOB(convertDateFormat(ex.dateOfBirth));
      if (ex.nhi && !nhi) setNhi(ex.nhi);
      if (ex.email && !patientEmail) setPatientEmail(ex.email);
      if (ex.phone && !patientPhone) setPatientPhone(ex.phone);
      if (ex.accNumber && !accNumber) setAccNumber(ex.accNumber);
      if (ex.injuryDate && !dateOfInjury) setDateOfInjury(convertDateFormat(ex.injuryDate));
      if (ex.injurySite && !injurySite) {
        const matched = matchInjurySite(ex.injurySite);
        if (matched) {
          setInjurySite(matched);
          // Also set side if extracted and the site has a side
          const siteConf = INJURY_SITES.find(s => s.value === matched);
          if (siteConf?.hasSide && ex.injurySide && !injurySide) {
            const side = ex.injurySide.toLowerCase().trim();
            if (['left', 'right', 'bilateral'].includes(side)) {
              setInjurySide(side);
            }
          }
        }
      }
      if (ex.reasonForReferral && !reasonForReferral) setReasonForReferral(ex.reasonForReferral);
      if (ex.clinicalNotes && !clinicalNotes) setClinicalNotes(ex.clinicalNotes);

      setExtractionDone(true);
    } catch (err) {
      console.error('Extraction error:', err);
    } finally {
      setExtracting(false);
    }
  };

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        setError(`File "${f.name}" exceeds 10MB limit`);
        return false;
      }
      return true;
    });

    if (files.length + validFiles.length > 5) {
      setError('Maximum 5 files allowed');
      return;
    }

    setFiles(prev => [...prev, ...validFiles]);
    setError('');
    e.target.value = ''; // Reset input

    // Auto-extract from the first file if we haven't already
    if (!extractionAttempted.current && validFiles.length > 0) {
      extractionAttempted.current = true;
      runExtraction(validFiles[0]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate required fields
    const missing = [];
    if (!accNumber.trim()) missing.push('ACC Number');
    if (!dateOfInjury) missing.push('Date of Injury');
    if (files.length === 0) missing.push('at least one supporting document');
    if (missing.length > 0) {
      setError('Please provide: ' + missing.join(', '));
      setLoading(false);
      return;
    }

    // Validate side if needed
    if (selectedSiteConfig?.hasSide && !injurySide) {
      setError('Please select the side (Left / Right / Bilateral)');
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('patientName', patientName);
      formData.append('patientDOB', patientDOB);
      formData.append('patientEmail', patientEmail);
      formData.append('patientPhone', patientPhone);
      formData.append('accNumber', accNumber);
      formData.append('nhi', nhi);
      formData.append('dateOfInjury', dateOfInjury);
      formData.append('injurySite', injurySite);
      formData.append('injurySide', injurySide);
      formData.append('reasonForReferral', reasonForReferral);
      formData.append('clinicalNotes', clinicalNotes);

      files.forEach(file => {
        formData.append('documents', file);
      });

      const res = await fetch(`${API_BASE_URL}/referrer-referrals/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Submission failed');
        return;
      }

      setSuccess(data);
    } catch (err) {
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#555',
    marginBottom: 5,
  };

  // Submissions switched off — show the notice instead of the form. Placed
  // ahead of the form render so navigating straight to /referrer/new does not
  // present a form the API will reject. All hooks are declared above this
  // point, so the early return is safe.
  if (!statusLoading && !submissionsEnabled) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f8' }}>
        <div style={{
          background: 'linear-gradient(135deg, #6b9bd1 0%, #4a7fb5 100%)',
          padding: '15px 20px',
          display: 'flex', alignItems: 'center', gap: 15,
        }}>
          <button
            onClick={() => navigate('/referrer/dashboard')}
            style={{
              backgroundColor: 'rgba(255,255,255,0.15)',
              color: 'white', border: 'none', borderRadius: 6,
              padding: '8px 12px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>
          <h1 style={{ color: 'white', margin: 0, fontSize: 20, fontWeight: 700 }}>
            New Referral
          </h1>
        </div>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
          <ReferralsDisabledNotice notice={notice} />
        </div>
      </div>
    );
  }

  // Success screen
  if (success) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f8', padding: 20 }}>
        <div style={{
          maxWidth: 500, margin: '60px auto',
          backgroundColor: 'white', borderRadius: 12,
          padding: 40, textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <div style={{
            width: 70, height: 70, borderRadius: '50%',
            backgroundColor: '#e8f5e9',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <CheckCircle size={36} color="#5cb85c" />
          </div>

          <h2 style={{ color: '#5cb85c', marginBottom: 10 }}>Referral Submitted!</h2>
          
          <p style={{ color: '#666', fontSize: 15, marginBottom: 20 }}>
            {success.message}
          </p>

          <div style={{
            backgroundColor: '#f5f5f5', borderRadius: 8,
            padding: 15, textAlign: 'left', marginBottom: 25, fontSize: 14,
          }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Patient:</strong> {success.referral?.patientName}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Injury:</strong> {success.referral?.injurySite}
              {success.referral?.injurySide && ` (${success.referral.injurySide})`}
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Status:</strong> {success.emailSent ? 'History form emailed to patient' : 'Received - processing'}
            </div>
            {success.referral?.documentsUploaded > 0 && (
              <div>
                <strong>Documents:</strong> {success.referral.documentsUploaded} file(s) uploaded
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => navigate('/referrer/dashboard')}
              style={{
                flex: 1, padding: '12px',
                backgroundColor: '#6b9bd1', color: 'white',
                border: 'none', borderRadius: 6, fontSize: 14,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => {
                setSuccess(null);
                setPatientName(''); setPatientDOB(''); setPatientEmail('');
                setPatientPhone(''); setAccNumber(''); setNhi('');
                setDateOfInjury(''); setInjurySite(''); setInjurySide('');
                setReasonForReferral(''); setClinicalNotes(''); setFiles([]);
                setExtractionDone(false); extractionAttempted.current = false;
              }}
              style={{
                flex: 1, padding: '12px',
                backgroundColor: '#e8941c', color: 'white',
                border: 'none', borderRadius: 6, fontSize: 14,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Submit Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f8' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #6b9bd1 0%, #4a7fb5 100%)',
        padding: '15px 20px',
        display: 'flex', alignItems: 'center', gap: 15,
      }}>
        <button
          onClick={() => navigate('/referrer/dashboard')}
          style={{
            backgroundColor: 'rgba(255,255,255,0.15)',
            color: 'white', border: 'none', borderRadius: 6,
            padding: '8px 12px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1 style={{ color: 'white', margin: 0, fontSize: 20, fontWeight: 700 }}>
          New Referral
        </h1>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
        {error && (
          <div style={{
            backgroundColor: '#fee', border: '1px solid #fcc', color: '#c33',
            padding: '12px 15px', borderRadius: 6, marginBottom: 15, fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Documents Section — first so OCR can auto-fill the rest */}
          <div style={{
            backgroundColor: 'white', borderRadius: 8, padding: 20,
            marginBottom: 15, border: '1px solid #eee',
          }}>
            <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Upload size={18} color="#6b9bd1" /> Supporting Documents *
            </h3>

            <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
              Upload your referral letter, ACC45 form, or other documents first — we'll auto-fill the form below. (PDF, Word, JPG, PNG — max 10MB each, up to 5 files)
            </p>

            {extracting && (
              <div style={{
                backgroundColor: '#e3f2fd', border: '1px solid #90caf9', color: '#1565c0',
                padding: '12px 15px', borderRadius: 6, marginBottom: 12, fontSize: 14,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Extracting details from document...
              </div>
            )}

            {extractionDone && !extracting && (
              <div style={{
                backgroundColor: '#e8f5e9', border: '1px solid #a5d6a7', color: '#2e7d32',
                padding: '12px 15px', borderRadius: 6, marginBottom: 12, fontSize: 14,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <CheckCircle size={16} />
                Details extracted from your document — please review the fields below
              </div>
            )}

            {files.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {files.map((file, index) => (
                  <div key={index} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', backgroundColor: '#f5f5f5',
                    borderRadius: 6, marginBottom: 6,
                  }}>
                    <FileText size={16} color="#6b9bd1" />
                    <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {file.name}
                    </span>
                    <span style={{ fontSize: 12, color: '#999' }}>{formatFileSize(file.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      style={{
                        backgroundColor: 'transparent', border: 'none',
                        cursor: 'pointer', padding: 4, display: 'flex',
                      }}
                    >
                      <X size={16} color="#c33" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {files.length < 5 && (
              <label style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, padding: '12px',
                border: '2px dashed #ddd', borderRadius: 6,
                cursor: 'pointer', color: '#888', fontSize: 14,
              }}>
                <Upload size={16} />
                Click to upload documents
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileAdd}
                  style={{ display: 'none' }}
                />
              </label>
            )}
          </div>

          {/* Patient Details Section */}
          <div style={{
            backgroundColor: 'white', borderRadius: 8, padding: 20,
            marginBottom: 15, border: '1px solid #eee',
          }}>
            <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
              <User size={18} color="#6b9bd1" /> Patient Details
            </h3>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Full Name *</label>
              <input type="text" value={patientName} onChange={e => setPatientName(e.target.value)} required style={inputStyle} placeholder="e.g. John Smith" />
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Date of Birth *</label>
                <input type="date" value={patientDOB} onChange={e => setPatientDOB(e.target.value)} required style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>NHI (if known)</label>
                <input type="text" value={nhi} onChange={e => setNhi(e.target.value)} style={inputStyle} placeholder="e.g. ABC1234" maxLength={7} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Email Address *</label>
              <input type="email" value={patientEmail} onChange={e => setPatientEmail(e.target.value)} required style={inputStyle} placeholder="patient@email.com" />
            </div>

            <div style={{ marginBottom: 0 }}>
              <label style={labelStyle}>Phone Number *</label>
              <input type="tel" value={patientPhone} onChange={e => setPatientPhone(e.target.value)} required style={inputStyle} placeholder="e.g. 021 123 4567" />
            </div>
          </div>

          {/* Injury Details Section */}
          <div style={{
            backgroundColor: 'white', borderRadius: 8, padding: 20,
            marginBottom: 15, border: '1px solid #eee',
          }}>
            <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Hash size={18} color="#e8941c" /> ACC & Injury Details
            </h3>

            <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>ACC Number *</label>
                <input type="text" value={accNumber} onChange={e => setAccNumber(e.target.value)} required style={inputStyle} placeholder="e.g. ACC12345" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Date of Injury *</label>
                <input type="date" value={dateOfInjury} onChange={e => setDateOfInjury(e.target.value)} required style={inputStyle} />
              </div>
            </div>

            <p style={{ fontSize: 12, color: '#b36b00', backgroundColor: '#fff8ec', border: '1px solid #ffe0a6', borderRadius: 4, padding: '8px 10px', margin: '0 0 12px', lineHeight: 1.4 }}>
              The ACC claim must be accepted prior to the patient's appointment. If the claim has not been accepted, the patient will be charged a private fee.
            </p>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Injury Site *</label>
              <select
                value={injurySite}
                onChange={e => { setInjurySite(e.target.value); setInjurySide(''); }}
                required
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Select injury site...</option>
                {INJURY_SITES.map(site => (
                  <option key={site.value} value={site.value}>{site.label}</option>
                ))}
              </select>
            </div>

            {selectedSiteConfig?.hasSide && (
              <div style={{ marginBottom: 0 }}>
                <label style={labelStyle}>Side *</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {['left', 'right', 'bilateral'].map(side => (
                    <button
                      key={side}
                      type="button"
                      onClick={() => setInjurySide(side)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: injurySide === side ? '2px solid #6b9bd1' : '1px solid #ddd',
                        borderRadius: 6,
                        backgroundColor: injurySide === side ? '#e3f2fd' : 'white',
                        color: injurySide === side ? '#6b9bd1' : '#666',
                        fontWeight: injurySide === side ? 600 : 400,
                        cursor: 'pointer',
                        textTransform: 'capitalize',
                        fontSize: 14,
                      }}
                    >
                      {side}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Clinical Details Section */}
          <div style={{
            backgroundColor: 'white', borderRadius: 8, padding: 20,
            marginBottom: 15, border: '1px solid #eee',
          }}>
            <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileText size={18} color="#5cb85c" /> Clinical Details
            </h3>

            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Reason for Referral *</label>
              <textarea
                value={reasonForReferral}
                onChange={e => setReasonForReferral(e.target.value)}
                required
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Brief description of why you are referring this patient..."
              />
            </div>

            <div style={{ marginBottom: 0 }}>
              <label style={labelStyle}>Clinical Notes (optional)</label>
              <textarea
                value={clinicalNotes}
                onChange={e => setClinicalNotes(e.target.value)}
                rows={4}
                style={{ ...inputStyle, resize: 'vertical' }}
                placeholder="Relevant clinical history, examination findings, investigations, treatments tried..."
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#5cb85c',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              marginBottom: 20,
            }}
          >
            {loading ? 'Submitting...' : 'Submit Referral'} <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default ReferrerNewReferral;
